import { components } from '@/types/api';
import { useAuth } from '@workos-inc/authkit-react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApiFetch } from './useApiFetch';

export type EnrichUser = {
  name: string;
} & components['schemas']['UserDetailsOutput'];

// Prefix of the query key backing `useEnrichUser` (the full key also includes
// the `apiFetch` instance). Kept as a shared constant so invalidation matches
// by prefix rather than duplicating the literal.
export const ENRICH_USER_QUERY_KEY = ['v1', 'me'] as const;

// Invalidates the cached `/me` response that backs `useEnrichAuth().user`,
// forcing a refetch. Use after mutations that change user state (e.g. identity
// verification) so gating flags like `is_identity_verified` update promptly.
export function invalidateEnrichUser(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: ENRICH_USER_QUERY_KEY });
}

export function useEnrichUser() {
  const { isLoading: workOSIsLoading, user: workOSUser } = useAuth();

  const { apiFetch, isLoading: apiFetchIsLoading } = useApiFetch();

  const { data: roleData, isLoading: roleDataIsLoading } = useQuery({
    queryKey: [...ENRICH_USER_QUERY_KEY, apiFetch],
    queryFn: async () => apiFetch.GET('/me') || null,
    select: (data) => {
      return data.data?.data || null;
    },
    enabled: !apiFetchIsLoading && workOSUser !== null,
  });

  const user = useMemo<EnrichUser | null>(() => {
    if (workOSIsLoading || apiFetchIsLoading || !roleData || !workOSUser) {
      return null;
    }
    return {
      ...roleData,
      name: `${workOSUser.firstName} ${workOSUser.lastName}`,
    };
  }, [workOSIsLoading, apiFetchIsLoading, roleData, workOSUser]);

  return {
    user,
    apiFetch,
    isLoading: workOSIsLoading || apiFetchIsLoading || roleDataIsLoading,
  };
}
